import type { AttentionItem, AttentionSummary, Company } from "./types";
import { quotationNeedsOwner, type Quotation } from "./reviewReferences";

/** An item the server parks on the owner (runtimeState "needs_approval") gets the normal
 *  Approve/Skip card whether or not Auto-approve is on. Auto-approve never parks in-scope
 *  work here — the server reports that as "queued" — so on an auto client this is only ever
 *  a gated build (awaitingOwner) or another request auto-approve may not start. Gating on
 *  !autoRevision used to leave those with no card, no count and no Approve/Skip while the
 *  sidebar still counted them (25 Sep incident). */
export function manualRevisionNeedsOwner(company: Pick<Company, "kind">, item: AttentionItem) {
  return company.kind !== "sample" && !item.approvalPending && item.kind !== "feature" && item.runtimeState === "needs_approval"
    && item.status !== "resolved" && item.stage !== "delivered";
}

export function revisionActivity(company: Pick<Company, "kind">, item: AttentionItem) {
  if (company.kind === "sample" || item.kind === "feature" || item.status === "resolved" || item.stage === "delivered") return null;
  if (item.approvalPending) return { label: item.approvalPending === "sending" ? "Sending Approval…" : "Approval Sent", active: false };
  switch (item.runtimeState) {
    case "running": return { label: "Working on Revision", active: true };
    case "preparing": return { label: "Preparing Revision", active: true };
    case "queued": return { label: "Revision Queued", active: false };
    case "paused": return { label: "Revision Paused", active: false };
    case "blocked": return { label: "Revision Needs Attention", active: false };
    case "waiting_client": return { label: "Waiting for Client", active: false };
    // Normally rendered as the Approve/Skip card instead (manualRevisionNeedsOwner), which
    // the review sheet and dock prefer; this label only ensures it is never status-less.
    case "needs_approval": return { label: "Needs Julian's approval", active: false };
    default: return null;
  }
}

/** Presentation only: retain acronyms/brand capitalization and exact stored terms. */
export function reviewTitle(value: string) {
  return value.replace(/(^|[\s—–-])([A-Za-z][A-Za-z0-9]*)/g, (_, prefix: string, word: string) =>
    prefix + (/[A-Z]/.test(word) ? word : word[0].toUpperCase() + word.slice(1)));
}

/** Dock and sheet use the same eligibility; the pill always names its displayed title. */
export function approvalDockState(companyId: string, companies: Company[],
  attention: Record<string, AttentionSummary>, quotations: Quotation[]) {
  const clients = companies.filter(c => c.kind !== "sample");
  const countFor = (company: Company) => quotations.filter(q => q.companyId === company.id && quotationNeedsOwner(q)).length
    + (attention[company.id]?.items ?? []).filter(item => manualRevisionNeedsOwner(company, item)).length;
  const current = clients.find(c => c.id === companyId);
  const count = current ? countFor(current) : 0, allCount = clients.reduce((sum, c) => sum + countFor(c), 0);
  const unpreparedFor = (id: string) => (attention[id]?.items ?? []).filter(item => item.kind === "feature"
    && item.status !== "resolved" && item.stage !== "delivered"
    && !quotations.some(q => q.companyId === id && q.threadId === item.threadId));
  const unprepared = unpreparedFor(companyId);
  const previewQuote = quotations.find(q => q.companyId === companyId && quotationNeedsOwner(q))
    ?? quotations.find(q => q.companyId === companyId && ["published", "publishing", "cancelling"].includes(q.status));
  const previewRevision = current ? (attention[companyId]?.items ?? []).find(item => manualRevisionNeedsOwner(current, item)) : undefined;
  const activity = current ? (attention[companyId]?.items ?? []).find(item => !manualRevisionNeedsOwner(current, item) && revisionActivity(current, item)) : undefined;
  const activityState = current && activity ? revisionActivity(current, activity) : null;
  const kind = previewQuote ? "Quotation" : previewRevision || activity ? "Revision" : unprepared.length ? "Quotation" : "Review";
  return {
    count, allCount, kind, activity: !previewQuote && !previewRevision ? activityState : null,
    title: previewQuote?.title ?? previewRevision?.title ?? activity?.title ?? unprepared[0]?.title ?? "Review Other Chats",
    // Other clients' work belongs in the overflow menu, never an empty chat's composer.
    hasWork: !!current && (count > 0 || !!previewQuote || !!activity),
  };
}

/** The server reads a claim it did not dispatch itself — work started from a chat
 *  message such as "approve all" — as "blocked" ("A claim exists, but this dashboard
 *  has not confirmed a running turn"). If that client's chat is mid-turn right now,
 *  the request IS being worked on: show it as working, not "Needs Attention". */
export function withChatActivity(attention: Record<string, AttentionSummary>, chatBusy: (companyId: string) => boolean) {
  let changed = false;
  const out: Record<string, AttentionSummary> = {};
  for (const [id, summary] of Object.entries(attention)) {
    if (!chatBusy(id) || !summary?.items?.some(item => item.runtimeState === "blocked" && item.claimActive)) { out[id] = summary; continue; }
    changed = true;
    out[id] = { ...summary, items: summary.items.map(item => item.runtimeState === "blocked" && item.claimActive
      ? { ...item, runtimeState: "running" as const, runtimeDetail: "This client's chat is working on it now (started from a chat message)." }
      : item) };
  }
  return changed ? out : attention;
}
